import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, Input, Output } from '@angular/core';
import { TsButtonModule, TSIconModule, TsInputComponent, TsPageHeaderModule, TsPaginatorModule } from 'tsui';
import { TsTableComponent } from 'tsui/lib/ts-table';

import { FormControl } from '@angular/forms';

import { AccountMapping, RegularAccount } from '../../models';
import { PaginatedAccountMapping } from '../../state/asset-management.state';
import { TsTabGroupComponent } from 'tsui/lib/ts-tab-group/ts-tab-group.component';
import { AccountMappingUtils } from '../../utils/account-mapping.utils';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'account-mapping-grid',
  standalone: true,
  imports: [
    TsInputComponent,
    TsPageHeaderModule,
    TsTableComponent,
    TSIconModule,
    TsButtonModule,
    TsPaginatorModule,
    TsTabGroupComponent,
  ],
  templateUrl: './account-mapping-grid.component.html',
  styleUrls: ['../../styles/asset-management-shared-styles.scss', './account-mapping-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountMappingGridComponent {
  readonly paginatedAccountMapping = input.required<PaginatedAccountMapping>();
  readonly unmappedAccounts = input.required<RegularAccount[]>();
  @Input({ required: true }) loading: boolean;

  @Output() importAccountMapping = new EventEmitter<void>();
  @Output() manageAccountMapping = new EventEmitter<AccountMapping>();
  @Output() deleteAccountMapping = new EventEmitter<AccountMapping>();
  @Output() createMappingFromAccount = new EventEmitter<RegularAccount>();

  readonly accountsFormFilter = new FormControl('');
  readonly accountsFormFilterChanges = toSignal(this.accountsFormFilter.valueChanges);

  readonly mappedAccounts = computed(() => {
    if (this.paginatedAccountMapping()) {
      return this.paginatedAccountMapping().results;
    }
  });
  readonly filteredMappedAccounts = computed(() => {
    if (this.mappedAccounts() && this.accountsFormFilterChanges()) {
      return this.mappedAccounts().filter((accountMapping) => {
        return accountMapping.gl_account.gl_account_name
          .toLowerCase()
          .includes(this.accountsFormFilter.value.toLowerCase());
      });
    } else {
      return this.mappedAccounts();
    }
  });

  readonly filteredUnmappedAccounts = computed(() => {
    if (this.unmappedAccounts() && this.accountsFormFilterChanges()) {
      return this.unmappedAccounts().filter((account) => {
        return account.gl_account_name.toLowerCase().includes(this.accountsFormFilter.value.toLowerCase());
      });
    } else {
      return this.unmappedAccounts();
    }
  });
  readonly unmappedAccountsTabLabel = computed(() => {
    const label = (count: number) => `Unmapped Accounts (${count})`;

    if (this.filteredUnmappedAccounts() && this.filteredMappedAccounts()) {
      return label(this.filteredUnmappedAccounts()?.length || 0);
    } else {
      return label(0);
    }
  });

  readonly mappedAccountColumns = AccountMappingUtils.mappedColumnDefs;
  readonly unmappedAccountColumns = AccountMappingUtils.unmappedColumnDefs;

  readonly mappedAccountActions = [
    {
      label: 'Edit Mapping',
      action: (accountMapping: AccountMapping) => {
        this.manageAccountMapping.emit(accountMapping);
      },
      icon: 'edit',
    },
    {
      icon: 'ts_trash-alt',
      label: 'Delete Mapping',
      variant: 'danger',
      action: (accountMapping: AccountMapping) => {
        this.deleteAccountMapping.emit(accountMapping);
      },
    },
  ];

  readonly unmappedAccountActions = [
    {
      label: 'Create Mapping',
      action: (regularAccount: RegularAccount) => {
        this.createMappingFromAccount.emit(regularAccount);
      },
      icon: 'ts_link',
    },
  ];
}
